import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import {
  ChevronRight,
  ChevronLeft,
  Plus,
  X,
  Copy,
  PartyPopper,
  Calendar,
  Clock,
  Key,
  CheckCircle2,
  Trash2
} from 'lucide-react';
import {
  ClientImplementation,
  CreateImplementationDTO,
  ImplementationPhase,
  ImplementationStatus,
  Department,
  Operator,
  BusinessRuleItem,
  BusinessRulesAcceptance,
  KanbanColumn,
  ImplementationTraining as ImplementationTrainingType,
} from '@/types/customerSupport';
import { customerSupportService as cssvc } from '@/services/customerSupportService';
import { apiService } from '@/services/api';

interface ImplementationWizardProps {
  clientId?: number;
  implementation?: ClientImplementation;
  /**
   * Salva os dados da implantação. Quando `options.keepOpen=true` o modal não
   * deve ser fechado pelo pai e a implantação deve ser recarregada para
   * espelhar os dados persistidos no formulário.
   */
  onSave: (
    data: CreateImplementationDTO,
    options?: { keepOpen?: boolean }
  ) => Promise<void>;
  onCancel: () => void;
  onDelete?: (id: number) => Promise<void>;
  /** Colunas do kanban (já ordenadas) — usadas para reordenar e renomear os steps do wizard */
  kanbanColumns?: KanbanColumn[];
}

// Função para gerar senha aleatória
const generateRandomPassword = (): string => {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let password = '';
  for (let i = 0; i < 12; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

const ImplementationWizard: React.FC<ImplementationWizardProps> = ({
  clientId,
  implementation,
  onSave,
  onCancel,
  onDelete,
  kanbanColumns
}) => {
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Treinamentos
  const [trainings, setTrainings] = useState<ImplementationTrainingType[]>([]);
  const [trainingsLoading, setTrainingsLoading] = useState(false);
  const [savingTraining, setSavingTraining] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Estados para modais de cadastro
  const [showDepartmentDialog, setShowDepartmentDialog] = useState(false);
  const [showOperatorDialog, setShowOperatorDialog] = useState(false);
  const [newDepartmentName, setNewDepartmentName] = useState('');
  const [newOperatorName, setNewOperatorName] = useState('');
  const [newOperatorDepartment, setNewOperatorDepartment] = useState('');
  
  // Estados para agendamentos ChatConnect
  const [chatConnectSchedules, setChatConnectSchedules] = useState<Array<{
    id?: number;
    user_id?: number;
    department_id?: number;
    user_name?: string;
    scheduled_at: string;
    notes?: string;
    reminder_email?: string;
    send_reminder_30min?: boolean;
  }>>([]);
  const [savingSchedule, setSavingSchedule] = useState<number | null>(null);

  // Estados para regras de negócio dinâmicas
  const [businessRules, setBusinessRules] = useState<BusinessRuleItem[]>([]);
  const [businessRulesLoading, setBusinessRulesLoading] = useState(false);
  const [interviewDate, setInterviewDate] = useState('');
  const [acceptanceData, setAcceptanceData] = useState<BusinessRulesAcceptance | null>(null);
  const [acceptanceLink, setAcceptanceLink] = useState('');
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Dados do wizard
  const [formData, setFormData] = useState<CreateImplementationDTO>({
    client_id: clientId || implementation?.client_id || 0,
    // ETAPA 1
    decision_maker_whatsapp: implementation?.decision_maker_whatsapp || '',
    send_weekly_roi_report: implementation?.send_weekly_roi_report || false,
    // ETAPA 2
    hiring_reason: implementation?.hiring_reason || '',
    tool_expectation: implementation?.tool_expectation || '',
    departments: implementation?.departments || [],
    operators: implementation?.operators || [],
    integration_with_portal: implementation?.integration_with_portal || false,
    integration_with_platform: implementation?.integration_with_platform || false,
    training_with: implementation?.training_with || null,
    training_type: implementation?.training_type || null,
    training_scheduled_at: implementation?.training_scheduled_at || null,
    // ETAPA 3
    management_system: implementation?.management_system || null,
    hinova_system_link: implementation?.hinova_system_link || '',
    hinova_user_atomos: implementation?.hinova_user_atomos || '',
    hinova_api_token: implementation?.hinova_api_token || '',
    south_token: implementation?.south_token || '',
    south_base_url: implementation?.south_base_url || '',
    other_system_notes: implementation?.other_system_notes || '',
    contacts: implementation?.contacts || [],
    send_welcome_how: implementation?.send_welcome_how || '',
    due_dates: implementation?.due_dates || '',
    reminder_how: implementation?.reminder_how || '',
    billing_how: implementation?.billing_how || '',
    grace_period_post_due: implementation?.grace_period_post_due || '',
    billing_frequency_post_due: implementation?.billing_frequency_post_due || '',
    send_birthday_message: implementation?.send_birthday_message || false,
    send_payment_confirmation: implementation?.send_payment_confirmation || false,
    // ETAPA 4
    has_bm: implementation?.has_bm ?? null,
    bm_verified: implementation?.bm_verified ?? null,
    infrastructure_notes: implementation?.infrastructure_notes || '',
    atomos_added_partner: implementation?.atomos_added_partner || false,
    has_facebook_profile: implementation?.has_facebook_profile || false,
    bm_company_form_filled: implementation?.bm_company_form_filled || false,
    bm_site_added: implementation?.bm_site_added || false,
    bm_sms_code_sent: implementation?.bm_sms_code_sent || false,
    bm_phone_for_sms: implementation?.bm_phone_for_sms || '',
    // ETAPA 5
    template_welcome: implementation?.template_welcome || '',
    template_reminder: implementation?.template_reminder || '',
    template_billing: implementation?.template_billing || '',
    template_inspection: implementation?.template_inspection || '',
    template_birthday: implementation?.template_birthday || '',
    template_others: implementation?.template_others || '',
    // ETAPA 6
    chatconnect_departments_created: implementation?.chatconnect_departments_created || [],
    chatconnect_users_created: implementation?.chatconnect_users_created || [],
    chatconnect_user_passwords: implementation?.chatconnect_user_passwords || {},
    // ETAPA 7
    chatbot_ai_instructions: implementation?.chatbot_ai_instructions || '',
    chatbot_compliance_done: implementation?.chatbot_compliance_done || false,
    chatbot_second_copy_working: implementation?.chatbot_second_copy_working || false,
    chatbot_faq_added: implementation?.chatbot_faq_added || false,
    chatbot_tests_done: implementation?.chatbot_tests_done || false,
    // ETAPA 8
    go_live_validated_with_decisor: implementation?.go_live_validated_with_decisor || false,
    go_live_validated_with_team: implementation?.go_live_validated_with_team || false,
    next_feedback_meeting_date: implementation?.next_feedback_meeting_date || '',
    current_phase: ImplementationPhase.KICK_OFF,
    status: 'IN_PROGRESS' as any
  });

  const baseSteps = [
    { id: 1, title: 'Kick-off & Dados Empresariais', phase: ImplementationPhase.KICK_OFF },
    { id: 2, title: 'Integração e Sistema', phase: ImplementationPhase.INTEGRATION },
    { id: 3, title: 'Regras de Negócio', phase: ImplementationPhase.BUSINESS_RULES },
    { id: 4, title: 'Infraestrutura Meta', phase: ImplementationPhase.INFRASTRUCTURE },
    { id: 5, title: 'Templates', phase: ImplementationPhase.TEMPLATES },
    { id: 6, title: 'ChatConnect', phase: ImplementationPhase.CHATCONNECT },
    { id: 7, title: 'Chatbot', phase: ImplementationPhase.CHATBOT },
    { id: 8, title: 'Go-Live', phase: ImplementationPhase.GO_LIVE }
  ];

  // Reordena os steps conforme a ordem das colunas do kanban (e usa o label do
  // kanban_columns quando o usuário renomeou). As colunas COMPLETED/CANCELLED
  // não fazem parte do wizard mesmo que apareçam no kanban.
  const steps = React.useMemo(() => {
    if (!kanbanColumns || kanbanColumns.length === 0) return baseSteps;

    const labelByKey = new Map<string, string>();
    const orderByKey = new Map<string, number>();
    kanbanColumns.forEach((col, idx) => {
      labelByKey.set(col.key, col.label);
      orderByKey.set(col.key, idx);
    });

    return [...baseSteps]
      .map(step => ({
        ...step,
        title: labelByKey.get(step.phase) || step.title,
      }))
      .sort((a, b) => {
        const oa = orderByKey.get(a.phase);
        const ob = orderByKey.get(b.phase);
        if (oa === undefined && ob === undefined) return 0;
        if (oa === undefined) return 1;
        if (ob === undefined) return -1;
        return oa - ob;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kanbanColumns]);

  // Função para encontrar o índice da etapa baseado no current_phase
  const getStepIndexByPhase = (phase: ImplementationPhase | undefined): number => {
    if (!phase) return 0;

    const stepIndex = steps.findIndex(step => step.phase === phase);
    // Se encontrar, retorna o índice, senão retorna 0 (primeira etapa)
    return stepIndex !== -1 ? stepIndex : 0;
  };

  // Inicializar currentStep quando a implementação mudar
  useEffect(() => {
    if (implementation?.current_phase) {
      const stepIndex = getStepIndexByPhase(implementation.current_phase);
      setCurrentStep(stepIndex);
      console.log(`[ImplementationWizard] Inicializando na etapa ${stepIndex + 1} (${steps[stepIndex]?.title}) baseado no current_phase: ${implementation.current_phase}`);
    }
  }, [implementation?.current_phase]);

  useEffect(() => {
    loadClients();
    if (implementation?.id) {
      loadChatConnectSchedules();
    }
  }, [implementation?.id]);

  useEffect(() => {
    // dispara o load de regras quando a fase ativa for BUSINESS_RULES,
    // independente da posição (índice) atual no stepper
    if (steps[currentStep]?.phase === ImplementationPhase.BUSINESS_RULES && implementation?.id) {
      loadBusinessRules();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, implementation?.id, steps]);

  // Carrega lista de treinamentos quando a implantação muda
  useEffect(() => {
    if (!implementation?.id) {
      setTrainings([]);
      return;
    }
    let mounted = true;
    setTrainingsLoading(true);
    cssvc.listTrainings(implementation.id)
      .then((list) => { if (mounted) setTrainings(list); })
      .catch((err) => console.error('[Wizard] erro ao listar treinamentos:', err))
      .finally(() => { if (mounted) setTrainingsLoading(false); });
    return () => { mounted = false; };
  }, [implementation?.id]);

  // Ressincroniza o formData quando o prop implementation mudar (após save+reload),
  // para que dados persistidos no backend voltem a aparecer no formulário.
  useEffect(() => {
    if (!implementation) return;
    setFormData(prev => ({
      ...prev,
      client_id: implementation.client_id ?? prev.client_id,
      decision_maker_whatsapp: implementation.decision_maker_whatsapp ?? prev.decision_maker_whatsapp,
      send_weekly_roi_report: implementation.send_weekly_roi_report ?? prev.send_weekly_roi_report,
      hiring_reason: implementation.hiring_reason ?? prev.hiring_reason,
      tool_expectation: implementation.tool_expectation ?? prev.tool_expectation,
      departments: implementation.departments ?? prev.departments,
      operators: implementation.operators ?? prev.operators,
      integration_with_portal: implementation.integration_with_portal ?? prev.integration_with_portal,
      integration_with_platform: implementation.integration_with_platform ?? prev.integration_with_platform,
      training_with: implementation.training_with ?? prev.training_with,
      training_type: implementation.training_type ?? prev.training_type,
      training_scheduled_at: implementation.training_scheduled_at ?? prev.training_scheduled_at,
      management_system: implementation.management_system ?? prev.management_system,
      hinova_system_link: implementation.hinova_system_link ?? prev.hinova_system_link,
      hinova_user_atomos: implementation.hinova_user_atomos ?? prev.hinova_user_atomos,
      hinova_api_token: implementation.hinova_api_token ?? prev.hinova_api_token,
      south_token: implementation.south_token ?? prev.south_token,
      south_base_url: implementation.south_base_url ?? prev.south_base_url,
      other_system_notes: implementation.other_system_notes ?? prev.other_system_notes,
      contacts: implementation.contacts ?? prev.contacts,
      send_welcome_how: implementation.send_welcome_how ?? prev.send_welcome_how,
      due_dates: implementation.due_dates ?? prev.due_dates,
      reminder_how: implementation.reminder_how ?? prev.reminder_how,
      billing_how: implementation.billing_how ?? prev.billing_how,
      grace_period_post_due: implementation.grace_period_post_due ?? prev.grace_period_post_due,
      billing_frequency_post_due: implementation.billing_frequency_post_due ?? prev.billing_frequency_post_due,
      send_birthday_message: implementation.send_birthday_message ?? prev.send_birthday_message,
      send_payment_confirmation: implementation.send_payment_confirmation ?? prev.send_payment_confirmation,
      has_bm: implementation.has_bm ?? prev.has_bm,
      bm_verified: implementation.bm_verified ?? prev.bm_verified,
      atomos_added_partner: implementation.atomos_added_partner ?? prev.atomos_added_partner,
      has_facebook_profile: implementation.has_facebook_profile ?? prev.has_facebook_profile,
      bm_company_form_filled: implementation.bm_company_form_filled ?? prev.bm_company_form_filled,
      bm_site_added: implementation.bm_site_added ?? prev.bm_site_added,
      bm_sms_code_sent: implementation.bm_sms_code_sent ?? prev.bm_sms_code_sent,
      bm_phone_for_sms: implementation.bm_phone_for_sms ?? prev.bm_phone_for_sms,
      infrastructure_notes: implementation.infrastructure_notes ?? prev.infrastructure_notes,
      template_welcome: implementation.template_welcome ?? prev.template_welcome,
      template_reminder: implementation.template_reminder ?? prev.template_reminder,
      template_billing: implementation.template_billing ?? prev.template_billing,
      template_inspection: implementation.template_inspection ?? prev.template_inspection,
      template_birthday: implementation.template_birthday ?? prev.template_birthday,
      template_others: implementation.template_others ?? prev.template_others,
      chatconnect_departments_created: implementation.chatconnect_departments_created ?? prev.chatconnect_departments_created,
      chatconnect_users_created: implementation.chatconnect_users_created ?? prev.chatconnect_users_created,
      chatconnect_user_passwords: implementation.chatconnect_user_passwords ?? prev.chatconnect_user_passwords,
      chatbot_ai_instructions: implementation.chatbot_ai_instructions ?? prev.chatbot_ai_instructions,
      chatbot_compliance_done: implementation.chatbot_compliance_done ?? prev.chatbot_compliance_done,
      chatbot_second_copy_working: implementation.chatbot_second_copy_working ?? prev.chatbot_second_copy_working,
      chatbot_faq_added: implementation.chatbot_faq_added ?? prev.chatbot_faq_added,
      chatbot_tests_done: implementation.chatbot_tests_done ?? prev.chatbot_tests_done,
      go_live_validated_with_decisor: implementation.go_live_validated_with_decisor ?? prev.go_live_validated_with_decisor,
      go_live_validated_with_team: implementation.go_live_validated_with_team ?? prev.go_live_validated_with_team,
      next_feedback_meeting_date: implementation.next_feedback_meeting_date ?? prev.next_feedback_meeting_date,
    }));
  }, [implementation?.id, implementation?.updated_at]);

  const loadBusinessRules = async () => {
    if (!implementation?.id) return;
    setBusinessRulesLoading(true);
    try {
      const { customerSupportService } = await import('@/services/customerSupportService');
      const [rules, acceptance] = await Promise.all([
        customerSupportService.getBusinessRules(implementation.id),
        customerSupportService.getBusinessRulesAcceptance(implementation.id),
      ]);
      setBusinessRules(rules);
      if (acceptance) {
        setAcceptanceData(acceptance);
        const baseUrl = window.location.origin;
        setAcceptanceLink(`${baseUrl}/aceite/${acceptance.token}`);
        if (acceptance.interview_date) {
          setInterviewDate(acceptance.interview_date.split('T')[0]);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar regras de negócio:', error);
    } finally {
      setBusinessRulesLoading(false);
    }
  };

  const handleSaveBusinessRules = async () => {
    if (!implementation?.id) return;
    try {
      const { customerSupportService } = await import('@/services/customerSupportService');
      const rules = businessRules
        .filter(r => r.rule_description)
        .map(r => ({
          client_product_id: r.client_product_id,
          rule_description: r.rule_description!,
        }));
      await customerSupportService.saveBusinessRules(implementation.id, rules);
      toast({ title: 'Regras de negócio salvas com sucesso!' });
    } catch (error) {
      toast({ title: 'Erro ao salvar regras', variant: 'destructive' });
    }
  };

  const handleGeneratePdf = async () => {
    if (!implementation?.id || !interviewDate) return;
    setGeneratingPdf(true);
    try {
      const { customerSupportService } = await import('@/services/customerSupportService');
      const result = await customerSupportService.generateBusinessRulesPdf(implementation.id, interviewDate);
      setAcceptanceLink(result.link);
      const acceptance = await customerSupportService.getBusinessRulesAcceptance(implementation.id);
      setAcceptanceData(acceptance);
      toast({ title: 'PDF gerado! Link de aceite disponível.' });
    } catch (error) {
      toast({ title: 'Erro ao gerar PDF', variant: 'destructive' });
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleUpdateRuleDescription = (clientProductId: number, value: string) => {
    setBusinessRules(prev => prev.map(r =>
      r.client_product_id === clientProductId
        ? { ...r, rule_description: value }
        : r
    ));
  };

  const loadChatConnectSchedules = async () => {
    if (!implementation?.id) return;
    try {
      const { customerSupportService } = await import('@/services/customerSupportService');
      const impl = await customerSupportService.getImplementation(implementation.id);
      if (impl.chatconnectSchedules) {
        setChatConnectSchedules(impl.chatconnectSchedules.map(s => ({
          id: s.id,
          user_id: s.user_id,
          department_id: s.department_id,
          user_name: s.user_name,
          scheduled_at: s.scheduled_at ? new Date(s.scheduled_at).toISOString().slice(0, 16) : '',
          notes: s.notes,
          reminder_email: s.reminder_email,
          send_reminder_30min: s.send_reminder_30min
        })));
      }
    } catch (error) {
      console.error('Erro ao carregar agendamentos:', error);
    }
  };

  const loadClients = async () => {
    try {
      const data = await apiService.getClients();
      setClients(data);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  const handleInputChange = (field: keyof CreateImplementationDTO, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddDepartment = () => {
    setNewDepartmentName('');
    setShowDepartmentDialog(true);
  };

  const handleSaveDepartment = () => {
    if (newDepartmentName.trim()) {
      setFormData(prev => ({
        ...prev,
        departments: [...(prev.departments || []), { name: newDepartmentName.trim() }]
      }));
      setShowDepartmentDialog(false);
      setNewDepartmentName('');
      toast({
        title: 'Departamento adicionado',
        description: `${newDepartmentName.trim()} foi adicionado com sucesso.`,
      });
    }
  };

  const handleRemoveDepartment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      departments: (prev.departments || []).filter((_, i) => i !== index)
    }));
  };

  const handleAddOperator = () => {
    if ((formData.departments || []).length === 0) {
      toast({
        title: 'Atenção',
        description: 'Adicione pelo menos um departamento antes de adicionar operadores.',
        variant: 'destructive'
      });
      return;
    }
    setNewOperatorName('');
    setNewOperatorDepartment('');
    setShowOperatorDialog(true);
  };

  const handleSaveOperator = () => {
    if (newOperatorName.trim() && newOperatorDepartment.trim()) {
      setFormData(prev => ({
        ...prev,
        operators: [...(prev.operators || []), { name: newOperatorName.trim(), department: newOperatorDepartment.trim() }]
      }));
      setShowOperatorDialog(false);
      setNewOperatorName('');
      setNewOperatorDepartment('');
      toast({
        title: 'Operador adicionado',
        description: `${newOperatorName.trim()} foi adicionado com sucesso.`,
      });
    }
  };

  const handleRemoveOperator = (index: number) => {
    setFormData(prev => ({
      ...prev,
      operators: (prev.operators || []).filter((_, i) => i !== index)
    }));
  };

  const handleGeneratePasswords = () => {
    const passwords: Record<string, string> = {};
    formData.operators?.forEach((op, index) => {
      const userId = `user_${index}_${op.name}`;
      passwords[userId] = generateRandomPassword();
    });
    handleInputChange('chatconnect_user_passwords', passwords);
    toast({
      title: 'Senhas geradas!',
      description: 'Senhas aleatórias foram geradas para todos os usuários.',
    });
  };

  const handleAddChatConnectSchedule = () => {
    setChatConnectSchedules(prev => [...prev, {
      scheduled_at: '',
      notes: '',
      reminder_email: '',
      send_reminder_30min: true
    }]);
  };

  const handleRemoveChatConnectSchedule = (index: number) => {
    setChatConnectSchedules(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveChatConnectSchedule = async (index: number) => {
    const schedule = chatConnectSchedules[index];
    if (!schedule.scheduled_at) {
      toast({
        title: 'Atenção',
        description: 'Preencha a data e hora do agendamento.',
        variant: 'destructive'
      });
      return;
    }

    if (!implementation?.id) {
      toast({
        title: 'Erro',
        description: 'Salve a implantação primeiro antes de agendar treinamentos.',
        variant: 'destructive'
      });
      return;
    }

    setSavingSchedule(index);
    try {
      const { customerSupportService } = await import('@/services/customerSupportService');
      const savedSchedule = await customerSupportService.createChatConnectSchedule(implementation.id, {
        implementation_id: implementation.id,
        user_id: schedule.user_id,
        department_id: schedule.department_id,
        user_name: schedule.user_name,
        scheduled_at: schedule.scheduled_at,
        notes: schedule.notes,
        reminder_email: schedule.reminder_email,
        send_reminder_30min: schedule.send_reminder_30min ?? true
      });

      // Atualizar o agendamento com o ID retornado
      const updated = [...chatConnectSchedules];
      updated[index] = { ...updated[index], id: savedSchedule.id };
      setChatConnectSchedules(updated);

      toast({
        title: 'Sucesso!',
        description: 'Agendamento salvo com sucesso. Os lembretes serão enviados automaticamente.',
      });
    } catch (error) {
      console.error('Erro ao salvar agendamento:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao salvar agendamento.',
        variant: 'destructive'
      });
    } finally {
      setSavingSchedule(null);
    }
  };

  const handleSaveTraining = async () => {
    if (!implementation?.id) {
      toast({ title: 'Salve a implantação antes de agendar treinamentos', variant: 'destructive' });
      return;
    }
    if (!formData.training_with || !formData.training_type || !formData.training_scheduled_at) {
      toast({ title: 'Preencha todos os campos do treinamento', variant: 'destructive' });
      return;
    }
    setSavingTraining(true);
    try {
      const created = await cssvc.createTraining(implementation.id, {
        training_with: formData.training_with as 'manager' | 'attendant',
        training_type: formData.training_type as 'initial' | 'review',
        scheduled_at: formData.training_scheduled_at,
      });
      setTrainings(prev => [...prev, created].sort(
        (a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime()
      ));
      // limpar campos para permitir cadastrar outro
      setFormData(prev => ({
        ...prev,
        training_with: null,
        training_type: null,
        training_scheduled_at: null,
      }));
      toast({ title: 'Agendamento salvo!' });
    } catch (err: any) {
      toast({
        title: 'Erro ao salvar agendamento',
        description: err?.response?.data?.error || err?.message || 'Tente novamente',
        variant: 'destructive',
      });
    } finally {
      setSavingTraining(false);
    }
  };

  const handleDeleteTraining = async (trainingId: number) => {
    if (!implementation?.id) return;
    try {
      await cssvc.deleteTraining(implementation.id, trainingId);
      setTrainings(prev => prev.filter(t => t.id !== trainingId));
      toast({ title: 'Agendamento removido' });
    } catch (err: any) {
      toast({
        title: 'Erro ao excluir agendamento',
        description: err?.message || 'Tente novamente',
        variant: 'destructive',
      });
    }
  };

  const handleSaveCurrentStep = async (options?: { silent?: boolean }) => {
    setLoading(true);
    try {
      const stepPhase = steps[currentStep]?.phase;
      const dataToSave = {
        ...formData,
        current_phase: stepPhase || formData.current_phase,
      };

      await onSave(dataToSave, { keepOpen: true });
      if (!options?.silent) {
        toast({
          title: 'Etapa salva!',
          description: `Etapa "${steps[currentStep].title}" salva com sucesso.`,
        });
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao salvar etapa atual.',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (currentStep >= steps.length - 1) return;
    // Salva a etapa atual antes de avançar — o usuário não precisa lembrar
    // de clicar em "Salvar Etapa Atual" antes de ir adiante.
    try {
      if (implementation?.id) {
        await handleSaveCurrentStep({ silent: true });
      }
      setCurrentStep(currentStep + 1);
    } catch {
      // erro já tratado em handleSaveCurrentStep
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = async () => {
    setLoading(true);
    try {
      // Conclui de fato: marca a fase como COMPLETED (card vai para a coluna
      // "Concluído") e o status como COMPLETED. Sem isso, o formData carrega os
      // valores iniciais congelados (KICK_OFF / IN_PROGRESS) e a implantação
      // voltava para a primeira coluna ainda "Em Andamento".
      await onSave({
        ...formData,
        current_phase: ImplementationPhase.COMPLETED,
        status: ImplementationStatus.COMPLETED,
      });
      toast({
        title: 'Sucesso!',
        description: 'Implantação concluída com sucesso!',
      });
      
      // Efeito visual de confetes usando CSS (sem dependência externa)
      if (typeof window !== 'undefined') {
        // Criar elementos de confete temporários
        const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A'];
        for (let i = 0; i < 50; i++) {
          const confetti = document.createElement('div');
          confetti.style.position = 'fixed';
          confetti.style.left = `${Math.random() * 100}%`;
          confetti.style.top = '-10px';
          confetti.style.width = '10px';
          confetti.style.height = '10px';
          confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
          confetti.style.borderRadius = '50%';
          confetti.style.pointerEvents = 'none';
          confetti.style.zIndex = '9999';
          confetti.style.animation = `confetti-fall ${1 + Math.random() * 2}s linear forwards`;
          document.body.appendChild(confetti);
          
          setTimeout(() => confetti.remove(), 3000);
        }
      }
      
      // Log JSON
      console.log('📋 Resumo da Implantação:', JSON.stringify(formData, null, 2));
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao salvar implantação.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getCompletedSteps = (): number[] => {
    const completed: number[] = [];
    
    // Etapa 1: Kick-off
    if (formData.client_id && formData.decision_maker_whatsapp) {
      completed.push(0);
    }
    
    // Etapa 2: Integração (toggles + treinamento agendado)
    if (
      formData.integration_with_portal ||
      formData.integration_with_platform ||
      formData.training_with ||
      formData.training_type ||
      formData.training_scheduled_at
    ) {
      completed.push(1);
    }

    // Etapa 3: Regras de Negócio (equipe agora vive aqui)
    if (
      formData.send_welcome_how ||
      formData.due_dates ||
      formData.reminder_how ||
      formData.billing_how ||
      (formData.departments && formData.departments.length > 0)
    ) {
      completed.push(2);
    }
    
    // Etapa 4: Infraestrutura
    if (formData.has_bm !== null || formData.has_facebook_profile) {
      completed.push(3);
    }
    
    // Etapa 5: Templates
    if (formData.template_welcome || formData.template_reminder || formData.template_billing || formData.template_others) {
      completed.push(4);
    }
    
    // Etapa 6: ChatConnect
    if ((formData.chatconnect_departments_created && formData.chatconnect_departments_created.length > 0) ||
        (formData.chatconnect_users_created && formData.chatconnect_users_created.length > 0) ||
        chatConnectSchedules.length > 0) {
      completed.push(5);
    }
    
    // Etapa 7: Chatbot
    if (formData.chatbot_ai_instructions || formData.chatbot_compliance_done || formData.chatbot_tests_done) {
      completed.push(6);
    }
    
    // Etapa 8: Go-Live
    if (formData.go_live_validated_with_decisor || formData.go_live_validated_with_team || formData.next_feedback_meeting_date) {
      completed.push(7);
    }
    
    return completed;
  };

  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return 'Não concluída';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStepRequirements = (stepIndex: number): string[] => {
    const requirements: string[] = [];
    const phase = steps[stepIndex]?.phase;

    switch (phase) {
      case ImplementationPhase.KICK_OFF:
        if (!formData.client_id) requirements.push('Selecionar cliente');
        if (!formData.decision_maker_whatsapp) requirements.push('Informar WhatsApp do decisor');
        break;
      case ImplementationPhase.INTEGRATION:
        if (!formData.integration_with_portal && !formData.integration_with_platform) {
          requirements.push('Selecionar pelo menos uma integração (portal ou plataforma)');
        }
        if (!formData.training_with) requirements.push('Selecionar com quem é o treinamento');
        if (!formData.training_type) requirements.push('Selecionar o tipo do treinamento');
        if (!formData.training_scheduled_at) requirements.push('Agendar data e hora do treinamento');
        break;
      case ImplementationPhase.BUSINESS_RULES:
        if (!formData.departments || formData.departments.length === 0) {
          requirements.push('Cadastrar pelo menos um departamento');
        }
        if (!formData.send_welcome_how) requirements.push('Definir como são enviadas boas-vindas');
        if (!formData.due_dates) requirements.push('Informar quais vencimentos');
        if (!formData.reminder_how) requirements.push('Definir como é enviado o lembrete');
        if (!formData.billing_how) requirements.push('Definir como é enviada a cobrança');
        break;
      case ImplementationPhase.INFRASTRUCTURE:
        if (formData.has_bm === null) requirements.push('Responder se cliente possui BM');
        if (formData.has_bm === true && formData.bm_verified === null) {
          requirements.push('Responder se BM está verificada');
        }
        if (formData.has_bm === false && !formData.has_facebook_profile) {
          requirements.push('Verificar se possui perfil no Facebook');
        }
        break;
      case ImplementationPhase.TEMPLATES:
        if (!formData.template_welcome && !formData.template_reminder && !formData.template_billing) {
          requirements.push('Cadastrar pelo menos um template (boas-vindas, lembrete ou cobrança)');
        }
        break;
      case ImplementationPhase.CHATCONNECT:
        if ((!formData.chatconnect_departments_created || formData.chatconnect_departments_created.length === 0) &&
            (!formData.chatconnect_users_created || formData.chatconnect_users_created.length === 0)) {
          requirements.push('Criar departamentos ou usuários no ChatConnect');
        }
        break;
      case ImplementationPhase.CHATBOT:
        if (!formData.chatbot_ai_instructions) requirements.push('Adicionar orientações para a IA');
        if (!formData.chatbot_compliance_done) requirements.push('Finalizar compliance');
        if (!formData.chatbot_tests_done) requirements.push('Realizar testes com departamentos');
        break;
      case ImplementationPhase.GO_LIVE:
        if (!formData.go_live_validated_with_decisor) requirements.push('Validar projeto com o decisor');
        if (!formData.go_live_validated_with_team) requirements.push('Validar projeto com a equipe');
        break;
    }

    return requirements;
  };

  const handleCopyProgress = () => {
    const client = clients.find(c => c.id === formData.client_id);
    const completedSteps = getCompletedSteps();
    // pendingSteps usa step.id-1 (índice lógico) porque getCompletedSteps
    // retorna índices lógicos estáveis (independente da ordem visual configurada).
    const pendingSteps = steps.filter(s => !completedSteps.includes(s.id - 1));

    // Mapa de índice lógico -> título exibido (respeita renomeações do kanban).
    const titleByLogicalIdx: Record<number, string> = {};
    baseSteps.forEach(b => {
      const visual = steps.find(s => s.id === b.id);
      titleByLogicalIdx[b.id - 1] = visual?.title || b.title;
    });

    const startDate = implementation?.created_at || new Date().toISOString();
    const stepDates = [
      implementation?.step1_completed_at,
      implementation?.step2_completed_at,
      implementation?.step3_completed_at,
      implementation?.step4_completed_at,
      implementation?.step5_completed_at,
      implementation?.step6_completed_at,
      implementation?.step7_completed_at,
      implementation?.step8_completed_at
    ];
    
    const progress = `
📋 PROGRESSO DA IMPLANTAÇÃO

👤 Cliente: ${client?.legal_name || 'N/A'}
📄 CNPJ: ${client?.company_tax_id || 'N/A'}

📅 INÍCIO DA IMPLANTAÇÃO: ${formatDate(startDate)}

---

✅ ETAPAS CONCLUÍDAS (${completedSteps.length}/${steps.length}):

${completedSteps.map(i => {
  const stepDate = stepDates[i];
  return `✓ ${titleByLogicalIdx[i] || ''}${stepDate ? ` - Concluída em ${formatDate(stepDate)}` : ''}`;
}).join('\n') || 'Nenhuma etapa concluída ainda'}

${pendingSteps.length > 0 ? `
⏳ ETAPAS PENDENTES (${pendingSteps.length}):

${pendingSteps.map((s, idx) => {
  const stepIndex = steps.findIndex(step => step.id === s.id);
  const requirements = getStepRequirements(stepIndex);
  let text = `○ ${s.title}`;
  if (requirements.length > 0) {
    text += `\n   📌 Para avançar: ${requirements.join(', ')}`;
  }
  return text;
}).join('\n\n')}
` : ''}

---

📊 DADOS PREENCHIDOS ATÉ AGORA:

${completedSteps.includes(0) ? `📊 ROI E RELATÓRIOS
WhatsApp Decisor: ${formData.decision_maker_whatsapp || 'N/A'}
Enviar Relatório Semanal: ${formData.send_weekly_roi_report ? 'Sim' : 'Não'}

` : ''}${completedSteps.includes(1) ? `🔗 INTEGRAÇÃO E SISTEMA
Integração com portal: ${formData.integration_with_portal ? 'Sim' : 'Não'}
Integração com a plataforma: ${formData.integration_with_platform ? 'Sim' : 'Não'}
Treinamento com: ${formData.training_with === 'manager' ? 'Gestor' : formData.training_with === 'attendant' ? 'Atendente' : 'N/A'}
Tipo de treinamento: ${formData.training_type === 'initial' ? 'Inicial' : formData.training_type === 'review' ? 'Revisão' : 'N/A'}
Treinamento agendado para: ${formData.training_scheduled_at || 'N/A'}

` : ''}${completedSteps.includes(2) ? `📊 REGRAS DE NEGÓCIO
Boas-vindas: ${formData.send_welcome_how || 'N/A'}
Vencimentos: ${formData.due_dates || 'N/A'}
Lembrete: ${formData.reminder_how || 'N/A'}
Cobrança: ${formData.billing_how || 'N/A'}
Carência: ${formData.grace_period_post_due || 'N/A'}
Frequência Cobrança: ${formData.billing_frequency_post_due || 'N/A'}
Mensagem Aniversário: ${formData.send_birthday_message ? 'Sim (R$0,40)' : 'Não'}
Reconhecimento Pagamento: ${formData.send_payment_confirmation ? 'Sim (R$0,05)' : 'Não'}

🏢 DEPARTAMENTOS
${(formData.departments || []).map(d => `- ${d.name}`).join('\n') || 'Nenhum cadastrado'}

👥 OPERADORES
${(formData.operators || []).map(o => `- ${o.name} (${o.department})`).join('\n') || 'Nenhum cadastrado'}

` : ''}${completedSteps.includes(3) ? `🛡️ INFRAESTRUTURA META
Possui BM: ${formData.has_bm === true ? 'Sim' : formData.has_bm === false ? 'Não' : 'Não respondido'}
BM Verificada: ${formData.bm_verified === true ? 'Sim' : formData.bm_verified === false ? 'Não' : 'Não respondido'}
Átomos como Parceiro: ${formData.atomos_added_partner ? 'Sim' : 'Não'}
Possui Perfil Facebook: ${formData.has_facebook_profile ? 'Sim' : 'Não'}
${formData.infrastructure_notes ? `Observações: ${formData.infrastructure_notes}\n` : ''}

` : ''}${completedSteps.includes(4) ? `📝 TEMPLATES
Boas-vindas: ${formData.template_welcome || 'N/A'}
Lembrete: ${formData.template_reminder || 'N/A'}
Cobrança: ${formData.template_billing || 'N/A'}
Revistoria: ${formData.template_inspection || 'N/A'}
Aniversário: ${formData.template_birthday || 'N/A'}
Outros: ${formData.template_others || 'N/A'}

` : ''}${completedSteps.includes(5) ? `💬 CHATCONNECT
Departamentos Criados: ${(formData.chatconnect_departments_created || []).length}
Usuários Criados: ${(formData.chatconnect_users_created || []).length}
Agendamentos: ${chatConnectSchedules.length}

` : ''}${completedSteps.includes(6) ? `🤖 CHATBOT
Compliance: ${formData.chatbot_compliance_done ? '✅' : '❌'}
2ª Via IA: ${formData.chatbot_second_copy_working ? '✅' : '❌'}
FAQ Adicionado: ${formData.chatbot_faq_added ? '✅' : '❌'}
Testes Realizados: ${formData.chatbot_tests_done ? '✅' : '❌'}

` : ''}${completedSteps.includes(7) ? `🚀 GO-LIVE
Validado com Decisor: ${formData.go_live_validated_with_decisor ? '✅' : '❌'}
Validado com Equipe: ${formData.go_live_validated_with_team ? '✅' : '❌'}
Próxima Reunião Feedback: ${formData.next_feedback_meeting_date || 'N/A'}

` : ''}

---

${pendingSteps.length > 0 ? `📌 PRÓXIMOS PASSOS:

${pendingSteps.slice(0, 3).map((s, idx) => {
  const stepIndex = steps.findIndex(step => step.id === s.id);
  const requirements = getStepRequirements(stepIndex);
  let text = `${idx + 1}. ${s.title}`;
  if (requirements.length > 0) {
    text += `\n   • ${requirements.join('\n   • ')}`;
  }
  return text;
}).join('\n\n')}

${pendingSteps.length > 3 ? `\n... e mais ${pendingSteps.length - 3} etapa(s) pendente(s)` : ''}` : '🎉 Todas as etapas foram concluídas! A implantação está completa.'}

${implementation?.total_duration_hours ? `
---

⏱️ MÉTRICAS DE TEMPO:

${stepDates.map((date, idx) => {
  if (!date) return null;
  const durationField = `step${idx + 1}_duration_hours` as keyof typeof implementation;
  const duration = implementation?.[durationField] as number | undefined;
  if (!duration) return null;
  const hours = Math.floor(duration);
  const minutes = Math.round((duration % 1) * 60);
  return `• ${steps[idx].title}: ${hours}h ${minutes}min`;
}).filter(Boolean).join('\n')}

⏱️ Tempo Total de Implantação: ${implementation.total_duration_hours ? `${Math.floor(implementation.total_duration_hours)}h ${Math.round((implementation.total_duration_hours % 1) * 60)}min (${parseFloat((implementation.total_duration_hours / 24).toFixed(1))} dias)` : 'N/A'}
` : ''}
    `.trim();
    
    navigator.clipboard.writeText(progress);
    toast({
      title: 'Progresso copiado!',
      description: 'Resumo do progresso copiado para a área de transferência.',
    });
  };

  const handleCopySummary = () => {
    const client = clients.find(c => c.id === formData.client_id);
    const startDate = implementation?.created_at || new Date().toISOString();
    const stepDates = [
      implementation?.step1_completed_at,
      implementation?.step2_completed_at,
      implementation?.step3_completed_at,
      implementation?.step4_completed_at,
      implementation?.step5_completed_at,
      implementation?.step6_completed_at,
      implementation?.step7_completed_at,
      implementation?.step8_completed_at
    ];
    
    const summary = `
📋 RESUMO COMPLETO DA IMPLANTAÇÃO

👤 Cliente: ${client?.legal_name || 'N/A'}
📄 CNPJ: ${client?.company_tax_id || 'N/A'}

📅 INÍCIO DA IMPLANTAÇÃO: ${formatDate(startDate)}

---

📊 ROI E RELATÓRIOS${stepDates[0] ? ` (Concluída em ${formatDate(stepDates[0])})` : ''}
WhatsApp Decisor: ${formData.decision_maker_whatsapp || 'N/A'}
Enviar Relatório Semanal: ${formData.send_weekly_roi_report ? 'Sim' : 'Não'}

🔗 INTEGRAÇÃO${stepDates[1] ? ` (Concluída em ${formatDate(stepDates[1])})` : ''}
Motivo da Contratação: ${formData.hiring_reason || 'N/A'}
Expectativa: ${formData.tool_expectation || 'N/A'}

🏢 DEPARTAMENTOS
${(formData.departments || []).map(d => `- ${d.name}`).join('\n') || 'Nenhum'}

👥 OPERADORES
${(formData.operators || []).map(o => `- ${o.name} (${o.department})`).join('\n') || 'Nenhum'}

📊 REGRAS DE NEGÓCIO${stepDates[2] ? ` (Concluída em ${formatDate(stepDates[2])})` : ''}
Boas-vindas: ${formData.send_welcome_how || 'N/A'}
Vencimentos: ${formData.due_dates || 'N/A'}
Lembrete: ${formData.reminder_how || 'N/A'}
Cobrança: ${formData.billing_how || 'N/A'}
Carência: ${formData.grace_period_post_due || 'N/A'}
Frequência Cobrança: ${formData.billing_frequency_post_due || 'N/A'}
Mensagem Aniversário: ${formData.send_birthday_message ? 'Sim (R$0,40)' : 'Não'}
Reconhecimento Pagamento: ${formData.send_payment_confirmation ? 'Sim (R$0,05)' : 'Não'}

🛡️ INFRAESTRUTURA META${stepDates[3] ? ` (Concluída em ${formatDate(stepDates[3])})` : ''}
Possui BM: ${formData.has_bm === true ? 'Sim' : formData.has_bm === false ? 'Não' : 'Não respondido'}
BM Verificada: ${formData.bm_verified === true ? 'Sim' : formData.bm_verified === false ? 'Não' : 'Não respondido'}
Átomos como Parceiro: ${formData.atomos_added_partner ? 'Sim' : 'Não'}
Possui Perfil Facebook: ${formData.has_facebook_profile ? 'Sim' : 'Não'}
${formData.infrastructure_notes ? `Observações: ${formData.infrastructure_notes}\n` : ''}

📝 TEMPLATES${stepDates[4] ? ` (Concluída em ${formatDate(stepDates[4])})` : ''}
Boas-vindas: ${formData.template_welcome || 'N/A'}
Lembrete: ${formData.template_reminder || 'N/A'}
Cobrança: ${formData.template_billing || 'N/A'}
Revistoria: ${formData.template_inspection || 'N/A'}
Aniversário: ${formData.template_birthday || 'N/A'}
Outros: ${formData.template_others || 'N/A'}

💬 CHATCONNECT${stepDates[5] ? ` (Concluída em ${formatDate(stepDates[5])})` : ''}
Departamentos Criados: ${(formData.chatconnect_departments_created || []).length}
Usuários Criados: ${(formData.chatconnect_users_created || []).length}
Agendamentos: ${chatConnectSchedules.length}

🤖 CHATBOT${stepDates[6] ? ` (Concluída em ${formatDate(stepDates[6])})` : ''}
Compliance: ${formData.chatbot_compliance_done ? '✅' : '❌'}
2ª Via IA: ${formData.chatbot_second_copy_working ? '✅' : '❌'}
FAQ Adicionado: ${formData.chatbot_faq_added ? '✅' : '❌'}
Testes Realizados: ${formData.chatbot_tests_done ? '✅' : '❌'}

🚀 GO-LIVE${stepDates[7] ? ` (Concluída em ${formatDate(stepDates[7])})` : ''}
Validado com Decisor: ${formData.go_live_validated_with_decisor ? '✅' : '❌'}
Validado com Equipe: ${formData.go_live_validated_with_team ? '✅' : '❌'}
Próxima Reunião Feedback: ${formData.next_feedback_meeting_date || 'N/A'}
    `.trim();
    
    navigator.clipboard.writeText(summary);
    toast({
      title: 'Copiado!',
      description: 'Resumo completo copiado para a área de transferência.',
    });
  };

  const renderStepContent = () => {
    // Switch baseado na fase do step ativo (não no índice), permitindo que a
    // ordem dos steps seja reconfigurada via kanban_columns sem quebrar o
    // mapeamento entre conteúdo e fase.
    const phase = steps[currentStep]?.phase;
    switch (phase) {
      case ImplementationPhase.KICK_OFF: // ETAPA 1: KICK-OFF
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="client_id">Cliente *</Label>
              <Select
                value={formData.client_id?.toString() || ''}
                onValueChange={(value) => handleInputChange('client_id', parseInt(value))}
                disabled={!!clientId || !!implementation}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id.toString()}>
                      {client.legal_name} - {client.company_tax_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Card className="border-primary/20 bg-primary/5">
              <CardHeader>
                <CardTitle className="text-lg">📊 Relatórios de ROI</CardTitle>
                <CardDescription>Configure o envio de relatórios para o decisor</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="decision_maker_whatsapp">
                    WhatsApp do Decisor (Para Relatórios de ROI) *
                  </Label>
                  <Input
                    id="decision_maker_whatsapp"
                    placeholder="(00) 00000-0000"
                    value={formData.decision_maker_whatsapp || ''}
                    onChange={(e) => handleInputChange('decision_maker_whatsapp', e.target.value)}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="send_weekly_roi_report"
                    checked={formData.send_weekly_roi_report}
                    onCheckedChange={(checked) => handleInputChange('send_weekly_roi_report', checked)}
                  />
                  <Label htmlFor="send_weekly_roi_report" className="cursor-pointer">
                    Enviar Relatório de Lucro Semanal (Sexta-feira) para este número?
                  </Label>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case ImplementationPhase.INTEGRATION: // ETAPA 2: INTEGRAÇÃO E SISTEMA
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Treinamento Agendado</CardTitle>
                <CardDescription>
                  Configure quem será treinado, o tipo e quando o treinamento acontecerá. Você pode salvar vários agendamentos para esta implantação.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="training_with">Treinamento com</Label>
                    <Select
                      value={formData.training_with || ''}
                      onValueChange={(v) => handleInputChange('training_with', v || null)}
                    >
                      <SelectTrigger id="training_with">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manager">Treinamento com gestor</SelectItem>
                        <SelectItem value="attendant">Treinamento com atendente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="training_type">Tipo de treinamento</Label>
                    <Select
                      value={formData.training_type || ''}
                      onValueChange={(v) => handleInputChange('training_type', v || null)}
                    >
                      <SelectTrigger id="training_type">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="initial">Treinamento inicial</SelectItem>
                        <SelectItem value="review">Treinamento de revisão</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="training_scheduled_at">Data e hora do treinamento</Label>
                  <Input
                    id="training_scheduled_at"
                    type="datetime-local"
                    value={(formData.training_scheduled_at || '').slice(0, 16)}
                    onChange={(e) => handleInputChange('training_scheduled_at', e.target.value || null)}
                  />
                </div>

                <div className="flex justify-end">
                  <Button
                    type="button"
                    onClick={handleSaveTraining}
                    disabled={
                      savingTraining ||
                      !implementation?.id ||
                      !formData.training_with ||
                      !formData.training_type ||
                      !formData.training_scheduled_at
                    }
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {savingTraining ? 'Salvando...' : 'Salvar agendamento'}
                  </Button>
                </div>

                <div className="border-t pt-4">
                  <Label className="text-sm font-semibold">Agendamentos salvos</Label>
                  {trainingsLoading ? (
                    <p className="text-sm text-muted-foreground mt-2">Carregando...</p>
                  ) : !implementation?.id ? (
                    <p className="text-sm text-muted-foreground mt-2">
                      Salve a implantação para começar a registrar agendamentos.
                    </p>
                  ) : trainings.length === 0 ? (
                    <p className="text-sm text-muted-foreground mt-2">
                      Nenhum agendamento registrado ainda.
                    </p>
                  ) : (
                    <div className="space-y-2 mt-2">
                      {trainings.map(t => (
                        <div
                          key={t.id}
                          className="flex items-center justify-between rounded-lg border p-3 bg-muted/30"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <Calendar className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium">
                                {new Date(t.scheduled_at).toLocaleString('pt-BR', {
                                  day: '2-digit', month: '2-digit', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit',
                                })}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {t.training_with === 'manager' ? 'Gestor' : 'Atendente'}
                                {' · '}
                                {t.training_type === 'initial' ? 'Inicial' : 'Revisão'}
                              </p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDeleteTraining(t.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Integrações</CardTitle>
                <CardDescription>
                  Marque as integrações que serão utilizadas neste cliente.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <Label htmlFor="integration_with_portal" className="cursor-pointer">
                    Integração com portal
                  </Label>
                  <Switch
                    id="integration_with_portal"
                    checked={!!formData.integration_with_portal}
                    onCheckedChange={(v) => handleInputChange('integration_with_portal', v)}
                  />
                </div>
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <Label htmlFor="integration_with_platform" className="cursor-pointer">
                    Integração com a plataforma
                  </Label>
                  <Switch
                    id="integration_with_platform"
                    checked={!!formData.integration_with_platform}
                    onCheckedChange={(v) => handleInputChange('integration_with_platform', v)}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case ImplementationPhase.BUSINESS_RULES: // ETAPA 3: REGRAS DE NEGÓCIO
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Sistema de Gestão</CardTitle>
                <CardDescription>
                  Identifique qual sistema é utilizado para gestão e informe os dados de acesso necessários para a integração.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="management_system">Qual sistema é utilizado para gestão?</Label>
                  <Select
                    value={formData.management_system || ''}
                    onValueChange={(v) => handleInputChange('management_system', v || null)}
                  >
                    <SelectTrigger id="management_system">
                      <SelectValue placeholder="Selecione o sistema" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hinova">Hinova</SelectItem>
                      <SelectItem value="south">South</SelectItem>
                      <SelectItem value="other">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.management_system === 'hinova' && (
                  <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
                    <div className="space-y-2">
                      <Label htmlFor="hinova_system_link">Link do sistema</Label>
                      <Input
                        id="hinova_system_link"
                        placeholder="https://..."
                        value={formData.hinova_system_link || ''}
                        onChange={(e) => handleInputChange('hinova_system_link', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="hinova_user_atomos">Usuário Átomos</Label>
                      <Input
                        id="hinova_user_atomos"
                        placeholder="Usuário cadastrado para a Átomos"
                        value={formData.hinova_user_atomos || ''}
                        onChange={(e) => handleInputChange('hinova_user_atomos', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="hinova_api_token">Token da API</Label>
                      <Input
                        id="hinova_api_token"
                        placeholder="Cole aqui o token da API"
                        value={formData.hinova_api_token || ''}
                        onChange={(e) => handleInputChange('hinova_api_token', e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {formData.management_system === 'south' && (
                  <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
                    <div className="space-y-2">
                      <Label htmlFor="south_token">Token</Label>
                      <Input
                        id="south_token"
                        placeholder="Token de acesso"
                        value={formData.south_token || ''}
                        onChange={(e) => handleInputChange('south_token', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="south_base_url">URL base</Label>
                      <Input
                        id="south_base_url"
                        placeholder="https://..."
                        value={formData.south_base_url || ''}
                        onChange={(e) => handleInputChange('south_base_url', e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {formData.management_system === 'other' && (
                  <div className="space-y-2 rounded-lg border bg-muted/30 p-4">
                    <Label htmlFor="other_system_notes">Informações do sistema</Label>
                    <Textarea
                      id="other_system_notes"
                      placeholder="Descreva qual sistema é, dados de acesso e qualquer informação relevante..."
                      rows={5}
                      value={formData.other_system_notes || ''}
                      onChange={(e) => handleInputChange('other_system_notes', e.target.value)}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Regras de Negócio</CardTitle>
                <CardDescription>Descreva como funciona cada produto vinculado a este cliente</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {businessRulesLoading ? (
                  <p className="text-muted-foreground">Carregando produtos...</p>
                ) : businessRules.length === 0 ? (
                  <div className="border border-dashed rounded-lg p-6 text-center text-muted-foreground">
                    Este cliente não possui produtos vinculados. Vincule produtos na aba "Produtos" antes de preencher as regras de negócio.
                  </div>
                ) : (
                  businessRules.map((rule) => (
                    <div key={rule.client_product_id} className="border rounded-lg p-4 bg-muted/30 space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="bg-green-500 text-white px-2 py-0.5 rounded text-xs font-semibold">ATIVO</span>
                        <h4 className="font-medium">{rule.product_name}</h4>
                      </div>
                      <div className="space-y-1">
                        <Label>Como funciona o {rule.product_name} para este cliente?</Label>
                        <Textarea
                          placeholder={`Descreva a regra de negócio do ${rule.product_name}...`}
                          rows={3}
                          value={rule.rule_description || ''}
                          onChange={(e) => handleUpdateRuleDescription(rule.client_product_id, e.target.value)}
                        />
                      </div>
                      {rule.observations && (
                        <p className="text-xs text-muted-foreground">
                          Observação do produto: <em>"{rule.observations}"</em>
                        </p>
                      )}
                    </div>
                  ))
                )}

                {businessRules.length > 0 && (
                  <div className="flex justify-end">
                    <Button onClick={handleSaveBusinessRules} variant="outline">
                      Salvar Regras
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Contatos</CardTitle>
                <CardDescription>
                  Informe os contatos do cliente para esta implantação. Indique se cada contato é novo ou já existente.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        contacts: [...(prev.contacts || []), { type: 'new', value: '' }],
                      }));
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar contato
                  </Button>
                </div>

                {(formData.contacts || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">
                    Nenhum contato adicionado.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {(formData.contacts || []).map((contact, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Select
                          value={contact.type}
                          onValueChange={(v) => {
                            const next = [...(formData.contacts || [])];
                            next[index] = { ...next[index], type: v as 'new' | 'existing' };
                            handleInputChange('contacts', next);
                          }}
                        >
                          <SelectTrigger className="w-[180px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">Novo contato</SelectItem>
                            <SelectItem value="existing">Contato existente</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="Nome / telefone / e-mail..."
                          value={contact.value}
                          onChange={(e) => {
                            const next = [...(formData.contacts || [])];
                            next[index] = { ...next[index], value: e.target.value };
                            handleInputChange('contacts', next);
                          }}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const next = (formData.contacts || []).filter((_, i) => i !== index);
                            handleInputChange('contacts', next);
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Hoje como é formada a sua equipe</CardTitle>
                <CardDescription>
                  Lembrando que com a integração da IA, muitas dúvidas comuns serão respondidas sem a necessidade humano, e também segunda-via de boleto.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Departamentos</Label>
                    <Button type="button" variant="outline" size="sm" onClick={handleAddDepartment}>
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(formData.departments || []).map((dept, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input value={dept.name} disabled />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveDepartment(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Operadores</Label>
                    <Button type="button" variant="outline" size="sm" onClick={handleAddOperator}>
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(formData.operators || []).map((op, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input value={`${op.name} (${op.department})`} disabled />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveOperator(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* PDF + Aceite Digital */}
            {businessRules.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">PDF e Aceite Digital</CardTitle>
                  <CardDescription>Gere o resumo das regras e envie o link de aceite ao cliente</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="interview_date">Data da entrevista de regras de negócio</Label>
                    <Input
                      id="interview_date"
                      type="date"
                      value={interviewDate}
                      onChange={(e) => setInterviewDate(e.target.value)}
                    />
                  </div>

                  <Button
                    onClick={handleGeneratePdf}
                    disabled={!interviewDate || generatingPdf}
                  >
                    {generatingPdf ? 'Gerando...' : 'Gerar PDF e Link de Aceite'}
                  </Button>

                  {acceptanceLink && (
                    <div className="space-y-2 p-3 bg-muted rounded-lg">
                      <Label>Link de aceite (envie ao cliente):</Label>
                      <div className="flex gap-2">
                        <Input value={acceptanceLink} readOnly />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(acceptanceLink);
                            toast({ title: 'Link copiado!' });
                          }}
                        >
                          Copiar
                        </Button>
                      </div>
                    </div>
                  )}

                  {acceptanceData && (
                    <div className="text-sm">
                      <strong>Status do aceite:</strong>{' '}
                      {acceptanceData.accepted_at ? (
                        <span className="text-green-600">
                          Aceito em {new Date(acceptanceData.accepted_at).toLocaleString('pt-BR')}
                        </span>
                      ) : (
                        <span className="text-yellow-600">Pendente</span>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        );

      case ImplementationPhase.INFRASTRUCTURE: // ETAPA 4: INFRAESTRUTURA META
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Infraestrutura Meta</CardTitle>
                <CardDescription>Checklist de configuração da Business Manager</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="has_bm">Cliente possui BM?</Label>
                  <Select
                    value={formData.has_bm === null ? 'not_answered' : formData.has_bm ? 'yes' : 'no'}
                    onValueChange={(value) => {
                      const boolValue = value === 'yes' ? true : value === 'no' ? false : null;
                      handleInputChange('has_bm', boolValue);
                      if (value === 'no') {
                        handleInputChange('bm_verified', false);
                        handleInputChange('atomos_added_partner', false);
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma opção" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Sim</SelectItem>
                      <SelectItem value="no">Não</SelectItem>
                      <SelectItem value="not_answered">Não respondido</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.has_bm === true && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="bm_verified">BM está verificada e Átomos adicionada como Parceiro na BM?</Label>
                      <Select
                        value={formData.bm_verified === null ? 'not_answered' : formData.bm_verified ? 'yes' : 'no'}
                        onValueChange={(value) => {
                          const boolValue = value === 'yes' ? true : value === 'no' ? false : null;
                          handleInputChange('bm_verified', boolValue);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione uma opção" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Sim</SelectItem>
                          <SelectItem value="no">Não</SelectItem>
                          <SelectItem value="not_answered">Não respondido</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}

                {formData.has_bm === false && (
                  <>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="has_facebook_profile"
                        checked={formData.has_facebook_profile}
                        onCheckedChange={(checked) => handleInputChange('has_facebook_profile', checked)}
                      />
                      <Label htmlFor="has_facebook_profile" className="cursor-pointer">
                        Possui perfil no Facebook para criarmos?
                      </Label>
                    </div>

                    {formData.has_facebook_profile && (
                      <div className="space-y-4 pl-6 border-l-2 border-yellow-200">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="bm_company_form_filled"
                            checked={formData.bm_company_form_filled}
                            onCheckedChange={(checked) => handleInputChange('bm_company_form_filled', checked)}
                          />
                          <Label htmlFor="bm_company_form_filled" className="cursor-pointer">
                            Ficha de empresa na BM preenchida
                          </Label>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="bm_site_added"
                            checked={formData.bm_site_added}
                            onCheckedChange={(checked) => handleInputChange('bm_site_added', checked)}
                          />
                          <Label htmlFor="bm_site_added" className="cursor-pointer">
                            Site incluído e validado
                          </Label>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="bm_phone_for_sms">Telefone para receber código SMS</Label>
                          <Input
                            id="bm_phone_for_sms"
                            placeholder="(00) 00000-0000"
                            value={formData.bm_phone_for_sms || ''}
                            onChange={(e) => handleInputChange('bm_phone_for_sms', e.target.value)}
                          />
                        </div>

                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="bm_sms_code_sent"
                            checked={formData.bm_sms_code_sent}
                            onCheckedChange={(checked) => handleInputChange('bm_sms_code_sent', checked)}
                          />
                          <Label htmlFor="bm_sms_code_sent" className="cursor-pointer">
                            Código via SMS enviado para o telefone informado
                          </Label>
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Importante:</strong> O token não é gerado neste momento, só quando subir o contato.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="infrastructure_notes">Observações da Equipe</Label>
                  <Textarea
                    id="infrastructure_notes"
                    placeholder="Registre aqui observações importantes sobre a infraestrutura Meta..."
                    rows={4}
                    value={formData.infrastructure_notes || ''}
                    onChange={(e) => handleInputChange('infrastructure_notes', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Campo para registro interno da equipe sobre a configuração da infraestrutura.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case ImplementationPhase.TEMPLATES: // ETAPA 5: TEMPLATES
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Templates</CardTitle>
                <CardDescription>
                  Cadastre os templates que o cliente deseja. Lembre-se que ele poderá não ser aprovado como utilidade conforme texto original.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="template_welcome">Boas-vindas</Label>
                  <Textarea
                    id="template_welcome"
                    placeholder="Cole aqui o template de boas-vindas..."
                    rows={4}
                    value={formData.template_welcome || ''}
                    onChange={(e) => handleInputChange('template_welcome', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="template_reminder">Lembrete</Label>
                  <Textarea
                    id="template_reminder"
                    placeholder="Cole aqui o template de lembrete..."
                    rows={4}
                    value={formData.template_reminder || ''}
                    onChange={(e) => handleInputChange('template_reminder', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="template_billing">Cobrança</Label>
                  <Textarea
                    id="template_billing"
                    placeholder="Cole aqui o template de cobrança..."
                    rows={4}
                    value={formData.template_billing || ''}
                    onChange={(e) => handleInputChange('template_billing', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="template_inspection">Revistoria</Label>
                  <Textarea
                    id="template_inspection"
                    placeholder="Cole aqui o template de revistoria..."
                    rows={4}
                    value={formData.template_inspection || ''}
                    onChange={(e) => handleInputChange('template_inspection', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="template_birthday">Aniversário</Label>
                  <Textarea
                    id="template_birthday"
                    placeholder="Cole aqui o template de aniversário..."
                    rows={4}
                    value={formData.template_birthday || ''}
                    onChange={(e) => handleInputChange('template_birthday', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="template_others">Outros Templates</Label>
                  <Textarea
                    id="template_others"
                    placeholder="Cole aqui outros templates..."
                    rows={4}
                    value={formData.template_others || ''}
                    onChange={(e) => handleInputChange('template_others', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case ImplementationPhase.CHATCONNECT: // ETAPA 6: CHATCONNECT
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>ChatConnect</CardTitle>
                <CardDescription>Checklist de criação de usuários e departamentos</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label className="mb-2 block">Departamentos Criados</Label>
                  <div className="space-y-2">
                    {(formData.departments || []).map((dept, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Checkbox
                          checked={(formData.chatconnect_departments_created || []).some(d => d.name === dept.name)}
                          onCheckedChange={(checked) => {
                            const created = formData.chatconnect_departments_created || [];
                            if (checked) {
                              handleInputChange('chatconnect_departments_created', [...created, dept]);
                            } else {
                              handleInputChange('chatconnect_departments_created', created.filter(d => d.name !== dept.name));
                            }
                          }}
                        />
                        <Label>{dept.name}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block">Usuários Criados</Label>
                  <div className="space-y-2">
                    {(formData.operators || []).map((op, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Checkbox
                          checked={(formData.chatconnect_users_created || []).some(u => u.name === op.name && u.department === op.department)}
                          onCheckedChange={(checked) => {
                            const created = formData.chatconnect_users_created || [];
                            if (checked) {
                              handleInputChange('chatconnect_users_created', [...created, op]);
                            } else {
                              handleInputChange('chatconnect_users_created', created.filter(u => !(u.name === op.name && u.department === op.department)));
                            }
                          }}
                        />
                        <Label>{op.name} ({op.department})</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <Label>Agendamentos de Treinamento</Label>
                    <Button type="button" variant="outline" size="sm" onClick={handleAddChatConnectSchedule}>
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar Agendamento
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {chatConnectSchedules.map((schedule, index) => (
                      <Card key={index}>
                        <CardContent className="pt-4">
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-2">
                                <Label>Data e Hora</Label>
                                <Input
                                  type="datetime-local"
                                  value={schedule.scheduled_at}
                                  onChange={(e) => {
                                    const updated = [...chatConnectSchedules];
                                    updated[index].scheduled_at = e.target.value;
                                    setChatConnectSchedules(updated);
                                  }}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Usuário/Departamento</Label>
                                <Input
                                  placeholder="Nome do usuário ou departamento"
                                  value={schedule.user_name || ''}
                                  onChange={(e) => {
                                    const updated = [...chatConnectSchedules];
                                    updated[index].user_name = e.target.value;
                                    setChatConnectSchedules(updated);
                                  }}
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label>Observações</Label>
                              <Textarea
                                placeholder="Observações sobre o treinamento..."
                                rows={2}
                                value={schedule.notes || ''}
                                onChange={(e) => {
                                  const updated = [...chatConnectSchedules];
                                  updated[index].notes = e.target.value;
                                  setChatConnectSchedules(updated);
                                }}
                              />
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                <Switch
                                  checked={schedule.send_reminder_30min ?? true}
                                  onCheckedChange={(checked) => {
                                    const updated = [...chatConnectSchedules];
                                    updated[index].send_reminder_30min = checked;
                                    setChatConnectSchedules(updated);
                                  }}
                                />
                                <Label className="cursor-pointer">
                                  Enviar e-mail de lembrete 30 minutos antes
                                </Label>
                              </div>
                              {schedule.send_reminder_30min && (
                                <div className="space-y-2">
                                  <Label htmlFor={`reminder-email-${index}`}>E-mail para lembrete</Label>
                                  <Input
                                    id={`reminder-email-${index}`}
                                    type="email"
                                    placeholder="exemplo@email.com"
                                    value={schedule.reminder_email || ''}
                                    onChange={(e) => {
                                      const updated = [...chatConnectSchedules];
                                      updated[index].reminder_email = e.target.value;
                                      setChatConnectSchedules(updated);
                                    }}
                                  />
                                  <p className="text-xs text-muted-foreground">
                                    Serão enviados 3 lembretes automaticamente: 2 horas antes, 30 minutos antes e no horário agendado.
                                  </p>
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2 pt-2">
                              <Button
                                type="button"
                                onClick={() => handleSaveChatConnectSchedule(index)}
                                disabled={savingSchedule === index || !schedule.scheduled_at}
                                className="flex-1"
                              >
                                {savingSchedule === index ? 'Salvando...' : 'Salvar Agendamento'}
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveChatConnectSchedule(index)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                            {schedule.id && (
                              <div className="text-xs text-green-600 flex items-center gap-1">
                                <CheckCircle2 className="h-3 w-3" />
                                Agendamento salvo - Lembretes ativos
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label>Gerar Senhas para Primeiro Login</Label>
                    <Button type="button" variant="outline" size="sm" onClick={handleGeneratePasswords}>
                      <Key className="h-4 w-4 mr-2" />
                      Gerar Senhas
                    </Button>
                  </div>
                  {Object.keys(formData.chatconnect_user_passwords || {}).length > 0 && (
                    <div className="mt-3 space-y-2">
                      {formData.operators?.map((op, index) => {
                        const userId = `user_${index}_${op.name}`;
                        const password = formData.chatconnect_user_passwords?.[userId];
                        if (!password) return null;
                        return (
                          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <span className="text-sm">{op.name}:</span>
                            <code className="text-sm font-mono bg-white px-2 py-1 rounded">{password}</code>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case ImplementationPhase.CHATBOT: // ETAPA 7: CHATBOT
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Chatbot</CardTitle>
                <CardDescription>Chatbot já foi desenvolvido conforme regra de negócio?</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="chatbot_ai_instructions">Orientações para a IA</Label>
                  <Textarea
                    id="chatbot_ai_instructions"
                    placeholder="Insira as orientações para a IA baseadas nas regras de negócio definidas..."
                    rows={8}
                    value={formData.chatbot_ai_instructions || ''}
                    onChange={(e) => handleInputChange('chatbot_ai_instructions', e.target.value)}
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="chatbot_compliance_done"
                      checked={formData.chatbot_compliance_done}
                      onCheckedChange={(checked) => handleInputChange('chatbot_compliance_done', checked)}
                    />
                    <Label htmlFor="chatbot_compliance_done" className="cursor-pointer">
                      Compliance feito?
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="chatbot_second_copy_working"
                      checked={formData.chatbot_second_copy_working}
                      onCheckedChange={(checked) => handleInputChange('chatbot_second_copy_working', checked)}
                    />
                    <Label htmlFor="chatbot_second_copy_working" className="cursor-pointer">
                      2-via com IA funcionando?
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="chatbot_faq_added"
                      checked={formData.chatbot_faq_added}
                      onCheckedChange={(checked) => handleInputChange('chatbot_faq_added', checked)}
                    />
                    <Label htmlFor="chatbot_faq_added" className="cursor-pointer">
                      Dúvidas frequentes adicionadas?
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="chatbot_tests_done"
                      checked={formData.chatbot_tests_done}
                      onCheckedChange={(checked) => handleInputChange('chatbot_tests_done', checked)}
                    />
                    <Label htmlFor="chatbot_tests_done" className="cursor-pointer">
                      Testes realizados com todos os departamentos e usuários?
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case ImplementationPhase.GO_LIVE: // ETAPA 8: GO-LIVE
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Go-Live</CardTitle>
                <CardDescription>Validação final antes de colocar em produção</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="go_live_validated_with_decisor"
                    checked={formData.go_live_validated_with_decisor}
                    onCheckedChange={(checked) => handleInputChange('go_live_validated_with_decisor', checked)}
                  />
                  <Label htmlFor="go_live_validated_with_decisor" className="cursor-pointer">
                    Validar todo o projeto com o decisor
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="go_live_validated_with_team"
                    checked={formData.go_live_validated_with_team}
                    onCheckedChange={(checked) => handleInputChange('go_live_validated_with_team', checked)}
                  />
                  <Label htmlFor="go_live_validated_with_team" className="cursor-pointer">
                    Validar todo o projeto com a equipe dele
                  </Label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="next_feedback_meeting_date">Data da Próxima Reunião para Coleta de Feedback</Label>
                  <Input
                    id="next_feedback_meeting_date"
                    type="datetime-local"
                    value={formData.next_feedback_meeting_date || ''}
                    onChange={(e) => handleInputChange('next_feedback_meeting_date', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Geralmente 1 semana após o Go-Live
                  </p>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button onClick={handleCopySummary} variant="outline" className="flex-1">
                    <Copy className="h-4 w-4 mr-2" />
                    Copiar Resumo para Grupo
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-6">
      {/* Stepper - clicável para navegar entre etapas */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div
                className="flex flex-col items-center flex-1 cursor-pointer group"
                onClick={() => setCurrentStep(index)}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                    index === currentStep
                      ? 'bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2'
                      : index < currentStep
                      ? 'bg-green-500 text-white group-hover:ring-2 group-hover:ring-green-300 group-hover:ring-offset-2'
                      : 'bg-gray-200 text-gray-600 group-hover:ring-2 group-hover:ring-gray-300 group-hover:ring-offset-2'
                  }`}
                >
                  {index < currentStep ? '✓' : index + 1}
                </div>
                <div className={`mt-2 text-xs text-center transition-all ${
                  index === currentStep ? 'font-semibold text-primary' : 'group-hover:font-medium'
                }`}>
                  {step.title}
                </div>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`h-1 flex-1 mx-2 ${
                    index < currentStep ? 'bg-green-500' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Content */}
      <Card>
        <CardHeader>
          <CardTitle>{steps[currentStep].title}</CardTitle>
        </CardHeader>
        <CardContent>
          {renderStepContent()}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <div className="flex gap-2">
          <Button variant="outline" onClick={currentStep === 0 ? onCancel : handlePrevious}>
            <ChevronLeft className="h-4 w-4 mr-2" />
            {currentStep === 0 ? 'Cancelar' : 'Anterior'}
          </Button>
          {implementation?.id && onDelete && (
            <Button
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={deleting}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {deleting ? 'Excluindo...' : 'Excluir Implantação'}
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSaveCurrentStep}
            disabled={loading}
          >
            {loading ? 'Salvando...' : 'Salvar Etapa Atual'}
          </Button>
          <Button
            variant="outline"
            onClick={handleCopyProgress}
          >
            <Copy className="h-4 w-4 mr-2" />
            Copiar Progresso
          </Button>
          {currentStep === steps.length - 1 ? (
            <Button onClick={handleFinish} disabled={loading} className="bg-green-600 hover:bg-green-700">
              {loading ? (
                'Concluindo...'
              ) : (
                <>
                  <PartyPopper className="h-4 w-4 mr-2" />
                  CONCLUIR IMPLANTAÇÃO
                </>
              )}
            </Button>
          ) : (
            <Button onClick={handleNext}>
              Próximo
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>

      {/* Confirmação de exclusão */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta implantação?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação é permanente. Todos os dados, observações, agendamentos e histórico desta implantação serão removidos. Não é possível desfazer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
              onClick={async (e) => {
                e.preventDefault();
                if (!implementation?.id || !onDelete) return;
                setDeleting(true);
                try {
                  await onDelete(implementation.id);
                  setShowDeleteConfirm(false);
                } catch (err) {
                  // o pai deve ter mostrado toast de erro; manter modal aberto para retry
                } finally {
                  setDeleting(false);
                }
              }}
            >
              {deleting ? 'Excluindo...' : 'Excluir definitivamente'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal para adicionar Departamento */}
      <Dialog open={showDepartmentDialog} onOpenChange={setShowDepartmentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Departamento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="department-name">Nome do departamento</Label>
              <Input
                id="department-name"
                placeholder="Ex: Financeiro, Suporte..."
                value={newDepartmentName}
                onChange={(e) => setNewDepartmentName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveDepartment();
                  }
                }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDepartmentDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveDepartment} disabled={!newDepartmentName.trim()}>
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal para adicionar Operador */}
      <Dialog open={showOperatorDialog} onOpenChange={setShowOperatorDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Operador</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="operator-name">Nome do operador</Label>
              <Input
                id="operator-name"
                placeholder="Ex: João Silva"
                value={newOperatorName}
                onChange={(e) => setNewOperatorName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="operator-department">Vincular ao Departamento</Label>
              <Select
                value={newOperatorDepartment}
                onValueChange={setNewOperatorDepartment}
                disabled={(formData.departments || []).length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um departamento cadastrado" />
                </SelectTrigger>
                <SelectContent>
                  {(formData.departments || []).map((dept, index) => (
                    <SelectItem key={index} value={dept.name}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.departments?.length === 0 ? (
                <p className="text-xs text-destructive">
                  ⚠️ Adicione departamentos primeiro antes de adicionar operadores.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Selecione um dos departamentos já cadastrados acima.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOperatorDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveOperator} 
              disabled={!newOperatorName.trim() || !newOperatorDepartment.trim()}
            >
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImplementationWizard;
